import React, { useEffect, useRef, useState } from 'react';
import Icon from './AppIcon';

/**
 * JitsiMeetRoom - Embeds a Jitsi Meet video room inside the platform via iframe.
 * 
 * Props:
 *   roomName  - unique room identifier (e.g. "StudyExpo-Session-123")
 *   displayName - participant's display name
 *   onClose   - callback when user clicks "Leave / Close"
 *   title     - optional header title
 */
const JitsiMeetRoom = ({ roomName, displayName = 'Participant', onClose, title }) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sanitize room name: only alphanumeric, hyphens, underscores
  const safeRoom = roomName
    ? roomName?.replace(/[^a-zA-Z0-9\-_]/g, '-')
    : `StudyExpo-Room-${Date.now()}`;

  const configParams = [
    `config.prejoinPageEnabled=false`,
    `config.startWithAudioMuted=false`,
    `config.startWithVideoMuted=false`,
    `config.requireDisplayName=false`,
    `config.lobby.enabled=false`,
    `config.lobby.autoKnockEnabled=false`,
    `config.startAsModerator=true`,
    `config.enableWelcomePage=false`,
    `config.disableDeepLinking=true`,
    `interfaceConfig.SHOW_JITSI_WATERMARK=false`,
    `interfaceConfig.TOOLBAR_BUTTONS=["microphone","camera","closedcaptions","desktop","fullscreen","fodeviceselection","hangup","chat","raisehand","videoquality","filmstrip","tileview"]`,
    `userInfo.displayName="${encodeURIComponent(displayName)}"`,
  ]?.join('&');

  const jitsiUrl = `https://meet.jit.si/${safeRoom}#${configParams}`;

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setError('Failed to load video room. Please check your internet connection.');
    setLoading(false);
  };

  return (
    <div className="flex flex-col w-full h-full bg-gray-900 rounded-xl overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 bg-error rounded-full animate-pulse" />
          <span className="text-white font-semibold text-sm truncate">
            {title || safeRoom}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 hidden sm:block">
            Powered by Jitsi Meet
          </span>
          {onClose && (
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-error hover:bg-error/80 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <Icon name="PhoneOff" size={13} />
              Leave
            </button>
          )}
        </div>
      </div>

      {/* Video area */}
      <div className="relative flex-1 min-h-0" ref={containerRef}>
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 z-10">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-gray-300 text-sm">Connecting to video room...</p>
            <p className="text-gray-500 text-xs mt-1">{safeRoom}</p>
          </div>
        )}

        {error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 z-10 p-6 text-center">
            <div className="w-14 h-14 bg-error/10 rounded-full flex items-center justify-center mb-4">
              <Icon name="VideoOff" size={28} color="var(--color-error)" />
            </div>
            <p className="text-white font-semibold mb-2">Connection Error</p>
            <p className="text-gray-400 text-sm mb-4">{error}</p>
            <button
              onClick={() => { setError(null); setLoading(true); }}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <iframe
            key={safeRoom}
            src={jitsiUrl}
            allow="camera; microphone; fullscreen; display-capture; autoplay"
            className="w-full h-full border-0"
            style={{ minHeight: '400px' }}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            title={`Jitsi Meet - ${safeRoom}`}
          />
        )}
      </div>
    </div>
  );
};

export default JitsiMeetRoom;
